import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { formatAmountKrw, GRANT_SUPPORT_FILTERS, REGION_NAMES, type GrantDetailDto, type GrantSupportFilter } from '@zoxo/shared';
import { Suspense } from 'react';

import { CommentList } from '../features/comments/comment-list';
import { commentsQuery } from '../features/comments/use-comments';
import { BookmarkToggle } from '../features/bookmarks/bookmark-toggle';
import { GrantReportButton } from '../features/grants/grant-report';
import { ErrorBoundary } from '../components/error-boundary';

import {
  GrantDetailAside,
  GrantDetailHead,
  GrantEligibility,
  GrantNoticeText,
  GrantSupportRows,
} from '../features/grants/grant-parts';
import { GrantBrief } from '../features/grants/grant-brief';
import { GrantTypeTabs } from '../features/grants/type-tabs';
import { PageContainer } from '../components/layout/page-container';
import { api } from '../lib/api';
import { orNotFound } from '../lib/or-not-found';
import { breadcrumbLd, pageHead, type PageMeta } from '../lib/seo';
import { canonical } from '../lib/site';

/**
 * 공고 상세 — 이 서비스의 **색인 단위**다.
 *
 * 검색으로 들어온 사람이 세 가지를 한 화면에서 알아야 한다: 얼마를 주는가,
 * 나한테 해당되는가, 언제까지인가. 그다음은 기관 원문으로 보낸다 — 우리는
 * 색인이고 요약이며, 신청은 기관에서 한다 (PRD §6 비목표).
 */
const grantQuery = (id: string) =>
  queryOptions({
    queryKey: ['grant', id],
    queryFn: () => api.get<GrantDetailDto>(`/grants/${id}`),
  });

/**
 * 검색 결과에 나갈 한 줄.
 *
 * **복제된 `summary` 를 쓰지 않는다.** 그 값은 출처 공고문 도입부 그대로라,
 * 검색 결과에 남의 글을 싣는 셈이었다. 브리프가 있으면 브리프, 없으면
 * 검증된 값으로 조립한다.
 */
function grantDescription(grant: GrantDetailDto): string {
  if (grant.brief) return clip(grant.brief, 160);
  const amount = formatAmountKrw(grant.amountMax);
  return [
    amount ? `지원금 ${amount}.` : null,
    grant.applyEnd ? `${grant.applyEnd}까지.` : '상시 접수.',
    grant.supportRegions.length ? grant.supportRegions.map((r) => REGION_NAMES[r as keyof typeof REGION_NAMES] ?? r).join(' · ') : null,
    grant.institution,
  ]
    .filter(Boolean)
    .join(' ')
    .slice(0, 160);
}

/** 단어 중간에서 끊지 않는다. 잘랐으면 말줄임표를 단다. */
function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${space > 0 ? cut.slice(0, space) : cut}…`;
}

/** 검색 결과 · 공유 카드에 보일 머리 — schema.org GovernmentService. */
function grantMeta(grant: GrantDetailDto): PageMeta {
  const url = canonical(`/news/${grant.id}`);
  const description = grantDescription(grant);
  return {
    title: grant.title,
    description,
    url,
    type: 'article',
    // 브리프가 없으면 우리 문장이 0자인 페이지다 — 색인하지 않는다(GrantListItemDto.hasBrief).
    noindex: !grant.hasBrief,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'GovernmentService',
        name: grant.title,
        description: description || grant.title,
        url,
        ...(grant.institution ? { provider: { '@type': 'GovernmentOrganization', name: grant.institution } } : {}),
        ...(grant.nationwide
          ? { areaServed: { '@type': 'Country', name: 'KR' } }
          : grant.supportRegions.length > 0
            ? {
                areaServed: grant.supportRegions.map((r) => ({
                  '@type': 'Place',
                  name: REGION_NAMES[r as keyof typeof REGION_NAMES] ?? r,
                })),
              }
            : {}),
        ...(grant.target ? { audience: { '@type': 'Audience', audienceType: grant.target } } : {}),
      },
      breadcrumbLd([
        { name: '지원사업', path: '/' },
        { name: grant.title, path: `/news/${grant.id}` },
      ]),
    ],
  };
}

export const Route = createFileRoute('/news/$id')({
  /*
    `?tab=` — 어느 섹션에서 눌러 들어왔는지(홈 유형 칸 · 유형 목록). 상단 기둥 알약이 그 섹션을
    켠다. 없으면 공고의 대표 유형. 정본 주소(canonical)는 `/news/<id>` 그대로라 색인은 하나다.
  */
  validateSearch: (search): { tab?: GrantSupportFilter } => {
    const tab = search.tab;
    return GRANT_SUPPORT_FILTERS.includes(tab as GrantSupportFilter) ? { tab: tab as GrantSupportFilter } : {};
  },
  loader: async ({ params: { id }, context: { queryClient } }) => {
    // 없는 공고는 404. loaderData 에는 머리만 싣는다 — 본문은 Query 캐시로 한 번만 넘어간다.
    const grant = await orNotFound(queryClient.ensureQueryData(grantQuery(id)));
    // 댓글은 실패해도 페이지를 막지 않는다. 공고 자체가 이 페이지의 본체다.
    await queryClient.ensureQueryData(commentsQuery({ kind: 'grant', id })).catch(() => undefined);
    return { meta: grantMeta(grant), now: Date.now() };
  },
  head: ({ loaderData }) => (loaderData ? pageHead(loaderData.meta) : {}),
  component: GrantDetailPage,
});

function Detail({ id }: { id: string }) {
  const { data: g } = useSuspenseQuery(grantQuery(id));
  // 마감 계산 기준 — loader 가 정한 시각이라 서버와 hydration 첫 렌더가 같다.
  const now = new Date(Route.useLoaderData().now);
  // 한 번 더 거른다 — 루트의 날것 search 가 새어 `?tab=bogus` 가 여기까지 온다(실측).
  const { tab } = Route.useSearch();
  const active = GRANT_SUPPORT_FILTERS.includes(tab as GrantSupportFilter) ? (tab as GrantSupportFilter) : g.primaryType;
  return (
    <article className="pb-6">
      {/*
        **상세에서도 기둥 알약이 사라지지 않는다.** 목록과 같은 자리(머리 바로 아래 sticky)에
        서서, 공고를 읽다가 다른 기둥으로 바로 건너간다. 켜진 알약은 눌러 들어온 섹션(`?tab=`),
        없으면 이 공고의 대표 유형.
      */}
      <div className="sticky top-12 z-[400] -mx-4 -mt-4 mb-4 border-b border-gray-100 bg-surface px-4 pb-2 pt-4 sm:top-[89px]">
        <GrantTypeTabs
          active={active}
          link={({ href, className, children }) => (
            <Link to={href} className={className}>{children}</Link>
          )}
        />
      </div>
      {/*
        머리·신청 자격은 우측 카드와 두 칸, 지원 내용부터는 전체 폭.
      */}
      <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
        <div>
          {/* 배지·제목·기본 정보 표. */}
          <GrantDetailHead grant={g} now={now} />

          <GrantEligibility detail={g.supportDetail} />
        </div>
        <GrantDetailAside
          grant={g}
          now={now}
          action={<BookmarkToggle grantId={g.id} isBookmarked={g.isBookmarked ?? false} />}
          footer={<GrantReportButton grantId={g.id} />}
        />
      </div>

      {/*
        **가공한 것이 먼저다.** 지원 내용은 전체 폭.

        해석기가 뽑고 검증 게이트가 원문에 대고 대조한 값을 항목마다 편다.
        원문은 그 아래 접어 둔다 — 8,000자를 먼저 펴 두면 우리가 뽑아낸
        「최대 5,000만원」이 그 아래 파묻힌다.
      */}
      {/* 우리 문장이 먼저다. 그다음이 원문에서 뽑아 대조한 값이다. */}
      <GrantBrief grant={g} />

      <GrantSupportRows detail={g.supportDetail} support={g.support} />

      <GrantNoticeText grant={g} />

      {/*
        **「어떻게 타먹지」가 여기서 지켜진다.**

        정부 사이트도 「언제 얼마」는 보여준다. 우리가 다른 점은 그 공고 밑에
        먼저 겪은 사람들의 말이 쌓인다는 것이다 — 어떻게 준비했는지, 어디서
        떨어졌는지. 우리가 쓰는 가이드가 아니라 서로에게 알려주는 것이라
        정부 사이트가 구조적으로 못 하는 일이다.

        마감된 공고에도 열어 둔다. 지원사업은 대부분 매년 반복되고, 작년
        공고의 스레드가 올해 신청자에게 가장 값진 자료다.
      */}
      <section className="mt-10">
        <ErrorBoundary
          resetKey={id}
          fallback={() => <p className="text-sm text-gray-500">댓글을 불러오지 못했어요.</p>}
        >
          <Suspense fallback={<p className="text-sm text-gray-400">댓글 불러오는 중…</p>}>
            <CommentList target={{ kind: 'grant', id }} />
          </Suspense>
        </ErrorBoundary>
      </section>

    </article>
  );
}

function GrantDetailPage() {
  const { id } = Route.useParams();
  return (
    /* overflow-hidden 이면 우측 카드의 sticky 가 죽는다. */
    <PageContainer allowOverflow>
      <Suspense
        fallback={
          <div className="space-y-3 pb-10" aria-label="불러오는 중">
            <div className="h-6 w-2/3 animate-pulse rounded bg-gray-100" />
            <div className="h-24 w-full animate-pulse rounded-xl bg-gray-100" />
          </div>
        }
      >
        <Detail id={id} />
      </Suspense>
    </PageContainer>
  );
}
